"use client";

import { useQuery } from "@tanstack/react-query";
import { getAdminPassword } from "@/lib/auth/storage";
import { adminApi } from "@/lib/api/admin";
import type {
  MonthlyRegister,
  MonthlyCell,
  MonthlySessionCell,
  RegisterFilter,
} from "@/lib/types";
import { isBeforeMorningFinalize, todayIST } from "@/lib/timezone";

export function useAdminRegister(date: string, filter: RegisterFilter) {
  const adminPassword = getAdminPassword();

  return useQuery({
    queryKey: ["admin", "register", date, filter],
    queryFn: () => adminApi.getRegister(adminPassword!, date, filter),
    enabled: !!adminPassword,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      if (data.morning_finalized) return 60_000;
      if (isBeforeMorningFinalize()) return 30_000;
      return 60_000;
    },
  });
}

export function useAdminDashboard() {
  const adminPassword = getAdminPassword();
  const date = todayIST();
  return useQuery({
    queryKey: ["admin", "dashboard", date],
    queryFn: () => adminApi.getDashboard(adminPassword!, date),
    enabled: !!adminPassword,
    refetchInterval: 30_000,
  });
}

export function useAdminPeople() {
  const adminPassword = getAdminPassword();
  return useQuery({
    queryKey: ["admin", "people"],
    queryFn: () => adminApi.listPeople(adminPassword!),
    enabled: !!adminPassword,
  });
}

export function useAdminNotices(seen?: boolean) {
  const adminPassword = getAdminPassword();
  return useQuery({
    queryKey: ["admin", "notices", seen],
    queryFn: () => adminApi.listNotices(adminPassword!, seen),
    enabled: !!adminPassword,
  });
}

export function useAdminMantras(
  view: "submitted" | "waived" | "overdue",
  options?: { enabled?: boolean }
) {
  const adminPassword = getAdminPassword();
  return useQuery({
    queryKey: ["admin", "mantras", view],
    queryFn: () => adminApi.listMantras(adminPassword!, view),
    enabled: !!adminPassword && (options?.enabled ?? true),
  });
}

export function useAdminMantraSummary(month: string) {
  const adminPassword = getAdminPassword();
  return useQuery({
    queryKey: ["admin", "mantras", "summary", month],
    queryFn: () => adminApi.getMantraMonthlySummary(adminPassword!, month),
    enabled: !!adminPassword && !!month,
  });
}

function normalizeMonthlyCell(
  cell: MonthlyCell & {
    blocks?: MonthlySessionCell[];
    shifts?: MonthlySessionCell[];
  }
): MonthlyCell {
  if (cell.sessions?.length) return cell;
  const nested = cell.blocks ?? cell.shifts;
  if (nested?.length) {
    const { blocks, shifts, ...rest } = cell;
    void blocks;
    void shifts;
    return { ...rest, sessions: nested };
  }
  return cell;
}

function normalizeMonthlyRegister(data: MonthlyRegister): MonthlyRegister {
  return {
    ...data,
    tables: data.tables.map((table) => ({
      ...table,
      rows: table.rows.map((row) => ({
        ...row,
        cells: Object.fromEntries(
          Object.entries(row.cells).map(([date, cell]) => [
            date,
            normalizeMonthlyCell(cell),
          ])
        ),
      })),
    })),
  };
}

export function useAdminMonthly(month: string) {
  const adminPassword = getAdminPassword();
  return useQuery({
    queryKey: ["admin", "monthly", month],
    queryFn: async () => {
      const data = await adminApi.getMonthlyRegister(adminPassword!, month);
      return normalizeMonthlyRegister(data);
    },
    enabled: !!adminPassword,
  });
}

export function useAdminHolidays(month: string) {
  const adminPassword = getAdminPassword();
  return useQuery({
    queryKey: ["admin", "holidays", month],
    queryFn: () => adminApi.listHolidays(adminPassword!, month),
    enabled: !!adminPassword,
  });
}

export function useAdminEmployeeShifts(date: string) {
  const adminPassword = getAdminPassword();
  return useQuery({
    queryKey: ["admin", "employee-shifts", date],
    queryFn: () => adminApi.listEmployeeShifts(adminPassword!, date),
    enabled: !!adminPassword,
  });
}
